/* Reviews page with sticky left summary column and scrollable right review list.
   Updated: Review score and count auto-calculated from published reviews via get_review_stats().
   Added Marktplaats profile link. Sticky left column with self-balancing top offset. */

import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Star } from 'lucide-react';
import { motion } from 'framer-motion';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import { supabase } from '@/lib/supabase';

interface Review {
  id: string;
  name: string;
  text: string;
  stars: number;
  created_at: string;
  display_order: number;
}

export default function ReviewsPage() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewScore, setReviewScore] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const [reviewsResult, statsResult] = await Promise.all([
        supabase
          .from('reviews')
          .select('*')
          .eq('is_published', true)
          .order('display_order', { ascending: true }),
        supabase.rpc('get_review_stats'),
      ]);

      if (reviewsResult.data) setReviews(reviewsResult.data);
      if (statsResult.data && statsResult.data.length > 0) {
        setReviewScore(parseFloat(statsResult.data[0].avg_score) || 0);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setIsLoading(false);
    }
  }

  const score = reviewScore;
  const count = reviews.length;
  const scoreFormatted = score.toFixed(1).replace('.', ',');

  const starCounts = [5, 4, 3, 2, 1].map((stars) => ({
    stars,
    count: reviews.filter((r) => r.stars === stars).length,
    percentage: count > 0 ? (reviews.filter((r) => r.stars === stars).length / count) * 100 : 0,
  }));

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderStars = (count: number, size: string = 'w-5 h-5') => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${star <= count ? 'fill-amber-400 text-amber-400' : 'fill-[#e7e1d6] text-[#e7e1d6]'}`}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Reviews | Buitn Planten</title>
        <meta name="description" content={`Lees ervaringen van klanten die bij Buitn Planten haagplanten, bomen en tuinplanten kochten. Beoordeeld met een ${scoreFormatted} / 5 door ${count} klanten.`} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Reviews | Buitn Planten" />
        <meta property="og:description" content={`Lees ervaringen van klanten die bij Buitn Planten kochten. Beoordeeld met een ${scoreFormatted} / 5.`} />
        <meta property="og:url" content="https://buitnplanten.nl/reviews" />
        <meta property="og:image" content="https://buitnplanten.nl/assets/sections/hero.webp" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://buitnplanten.nl/reviews" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Buitn Planten",
            "url": "https://buitnplanten.nl",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": score.toFixed(1),
              "bestRating": "5",
              "ratingCount": count,
            },
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#fcfaf6]">
        <Header />

        <main className="mx-auto max-w-[1240px] px-5 pt-8 pb-20 lg:px-8">
          <PageBreadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Reviews' }]} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mb-10 mt-4"
          >
            <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">
              Wat klanten zeggen
            </p>
            <h1 className="text-4xl tracking-[-0.035em] text-[#1e2b22] md:text-5xl">
              Reviews
            </h1>
            <p className="mt-3 max-w-xl text-base leading-7 text-[#5a6552] sm:text-lg sm:leading-8">
              Ervaringen van klanten die met ons aan de slag gingen in hun buitenruimte.
            </p>
          </motion.div>

          {isLoading ? (
            <div className="flex items-center justify-center py-32">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[#1e2b22]"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[340px_1fr]">
              {/* Left column — sticky summary */}
              <div className="lg:sticky lg:top-[200px] lg:h-fit">
                <div className="rounded-2xl bg-white p-8 ring-1 ring-[#e7e1d6]">
                  <div className="flex items-end gap-2">
                    <span className="text-5xl font-bold tracking-[-0.03em] text-[#1e2b22]">
                      {scoreFormatted}
                    </span>
                    <span className="mb-1.5 text-lg text-[#5a6552]">/ 5</span>
                  </div>
                  <div className="mt-3">
                    {renderStars(Math.round(score), 'w-6 h-6')}
                  </div>
                  <p className="mt-3 text-sm text-[#5a6552]">
                    Gebaseerd op {count} beoordelingen
                  </p>

                  <div className="mt-6 space-y-2.5">
                    {starCounts.map(({ stars, count: starCount, percentage }) => (
                      <div key={stars} className="flex items-center gap-3">
                        <span className="flex w-12 shrink-0 items-center gap-1 text-xs text-[#5a6552]">
                          {stars} <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                        </span>
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#f2ede4]">
                          <div
                            className="h-full rounded-full bg-amber-400 transition-all duration-700"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="w-8 shrink-0 text-right text-xs text-[#5a6552]">
                          {starCount}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 flex flex-col gap-2">
                    <a
                      href="https://share.google/rRuGOX9cwYp6vbKci"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium text-[#1e2b22] underline underline-offset-4 hover:text-[#5a6552] transition-colors"
                    >
                      Bekijk ons Google-profiel
                    </a>
                    <a
                      href="https://www.marktplaats.nl/smb-profile/profile/42090255/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium text-[#1e2b22] underline underline-offset-4 hover:text-[#5a6552] transition-colors"
                    >
                      Bekijk ons Marktplaats-profiel
                    </a>
                  </div>
                </div>
              </div>

              {/* Right column — scrollable review list */}
              <div className="space-y-6">
                {reviews.length === 0 ? (
                  <div className="rounded-2xl bg-white p-12 text-center ring-1 ring-[#e7e1d6]">
                    <p className="text-lg text-[#5a6552]">
                      Nog geen reviews beschikbaar. Kom binnenkort terug!
                    </p>
                  </div>
                ) : (
                  reviews.map((review, index) => (
                    <motion.article
                      key={review.id}
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: Math.min(index * 0.06, 0.3), ease: [0.22, 1, 0.36, 1] }}
                      viewport={{ once: true, margin: '-50px' }}
                      className="rounded-2xl bg-white p-7 ring-1 ring-[#e7e1d6] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(30,43,34,0.08)]"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#f2ede4] text-sm font-semibold text-[#1e2b22]">
                            {review.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-[#1e2b22]">{review.name}</p>
                            <p className="text-xs text-[#5a6552]">
                              {formatDate(review.created_at)}
                            </p>
                          </div>
                        </div>
                        {renderStars(review.stars)}
                      </div>
                      <p className="mt-4 text-[15px] leading-7 text-[#5a6552]">
                        {review.text}
                      </p>
                    </motion.article>
                  ))
                )}
              </div>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}
